import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from '../ui/button';

const COOKIE_CONSENT_KEY = 'aravell_cookie_consent';

const translations = {
  ca: {
    title: 'Utilitzem cookies',
    description: 'Aquest lloc web utilitza cookies per millorar la vostra experiència. Les cookies tècniques són essencials per al funcionament del lloc. Les cookies d\'anàlisi ens ajuden a entendre com utilitzeu el lloc.',
    acceptAll: 'Acceptar tot',
    acceptEssential: 'Només essencials',
    customize: 'Personalitzar',
    moreInfo: 'Més informació',
    save: 'Guardar preferències',
    essential: 'Cookies essencials',
    essentialDesc: 'Necessàries per al funcionament bàsic del lloc web. No es poden desactivar.',
    analytics: 'Cookies d\'anàlisi',
    analyticsDesc: 'Ens ajuden a entendre com els visitants interactuen amb el lloc web (Google Analytics).',
    preferences: 'Cookies de preferències',
    preferencesDesc: 'Permeten recordar les vostres preferències com l\'idioma escollit.'
  },
  es: {
    title: 'Utilizamos cookies',
    description: 'Este sitio web utiliza cookies para mejorar su experiencia. Las cookies técnicas son esenciales para el funcionamiento del sitio. Las cookies de análisis nos ayudan a entender cómo utiliza el sitio.',
    acceptAll: 'Aceptar todo',
    acceptEssential: 'Solo esenciales',
    customize: 'Personalizar',
    moreInfo: 'Más información',
    save: 'Guardar preferencias',
    essential: 'Cookies esenciales',
    essentialDesc: 'Necesarias para el funcionamiento básico del sitio web. No se pueden desactivar.',
    analytics: 'Cookies de análisis',
    analyticsDesc: 'Nos ayudan a entender cómo los visitantes interactúan con el sitio web (Google Analytics).',
    preferences: 'Cookies de preferencias',
    preferencesDesc: 'Permiten recordar sus preferencias como el idioma elegido.'
  },
  fr: {
    title: 'Nous utilisons des cookies',
    description: 'Ce site web utilise des cookies pour améliorer votre expérience. Les cookies techniques sont essentiels au fonctionnement du site. Les cookies d\'analyse nous aident à comprendre comment vous utilisez le site.',
    acceptAll: 'Tout accepter',
    acceptEssential: 'Essentiels uniquement',
    customize: 'Personnaliser',
    moreInfo: 'Plus d\'informations',
    save: 'Enregistrer les préférences',
    essential: 'Cookies essentiels',
    essentialDesc: 'Nécessaires au fonctionnement de base du site web. Ne peuvent pas être désactivés.',
    analytics: 'Cookies d\'analyse',
    analyticsDesc: 'Nous aident à comprendre comment les visiteurs interagissent avec le site web (Google Analytics).',
    preferences: 'Cookies de préférences',
    preferencesDesc: 'Permettent de mémoriser vos préférences comme la langue choisie.'
  },
  en: {
    title: 'We use cookies',
    description: 'This website uses cookies to improve your experience. Technical cookies are essential for the site to function. Analytics cookies help us understand how you use the site.',
    acceptAll: 'Accept all',
    acceptEssential: 'Essential only',
    customize: 'Customize',
    moreInfo: 'More information',
    save: 'Save preferences',
    essential: 'Essential cookies',
    essentialDesc: 'Necessary for the basic functioning of the website. Cannot be disabled.',
    analytics: 'Analytics cookies',
    analyticsDesc: 'Help us understand how visitors interact with the website (Google Analytics).',
    preferences: 'Preference cookies',
    preferencesDesc: 'Allow us to remember your preferences such as the chosen language.'
  }
};

export const CookieBanner = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.ca;
  
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be changed
    analytics: false,
    preferences: true
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to not show immediately on page load
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
        // Apply analytics consent
        if (savedPrefs.analytics && window.gtag) {
          window.gtag('consent', 'update', {
            'analytics_storage': 'granted'
          });
        }
      } catch {
        // Invalid consent data, show banner again
        setVisible(true);
      }
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setVisible(false);
    setShowSettings(false);
    
    // Update Google Analytics consent if available
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': prefs.analytics ? 'granted' : 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      preferences: true
    });
  };

  const handleAcceptEssential = () => {
    saveConsent({
      essential: true,
      analytics: false,
      preferences: true
    });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  // Function to open settings from footer link
  useEffect(() => {
    const handleOpenSettings = () => {
      setVisible(true);
      setShowSettings(true);
    };
    
    window.openCookieSettings = handleOpenSettings;
    
    return () => {
      delete window.openCookieSettings;
    };
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-white border border-[#E5E5E5] shadow-2xl rounded-xl overflow-hidden">
            {!showSettings ? (
              // Main banner view
              <div className="p-5 md:p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#1A4D2E]/10 rounded-full flex items-center justify-center">
                    <Cookie className="w-5 h-5 text-[#1A4D2E]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-2">
                      {t.title}
                    </h3>
                    <p className="text-sm text-[#666666] mb-4 leading-relaxed">
                      {t.description}{' '}
                      <Link 
                        to="/legal/cookies" 
                        className="text-[#1A4D2E] hover:text-[#D4AF37] underline transition-colors"
                      >
                        {t.moreInfo}
                      </Link>
                    </p>
                    
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={handleAcceptAll}
                        className="bg-[#1A4D2E] hover:bg-[#153d25] text-white gap-2"
                        data-testid="cookie-accept-all"
                      >
                        <Check className="w-4 h-4" />
                        {t.acceptAll}
                      </Button>
                      <Button
                        onClick={handleAcceptEssential}
                        variant="outline"
                        className="border-[#E5E5E5] hover:border-[#1A4D2E]"
                        data-testid="cookie-accept-essential"
                      >
                        {t.acceptEssential}
                      </Button>
                      <Button
                        onClick={() => setShowSettings(true)}
                        variant="ghost"
                        className="text-[#666666] hover:text-[#1A4D2E] gap-2"
                        data-testid="cookie-customize"
                      >
                        <Settings className="w-4 h-4" />
                        {t.customize}
                      </Button>
                    </div>
                  </div>
                  <button
                    onClick={handleAcceptEssential}
                    className="flex-shrink-0 p-1 text-[#999999] hover:text-[#333333] transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              // Settings view
              <div className="p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-lg font-semibold text-[#1A1A1A] flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    {t.customize}
                  </h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="p-1 text-[#999999] hover:text-[#333333] transition-colors"
                    aria-label="Back"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-4 mb-6">
                  {/* Essential cookies */}
                  <div className="flex items-start gap-4 p-4 bg-[#F9F9F7] rounded-lg">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-[#1A4D2E] cursor-not-allowed"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-[#333333] text-sm">{t.essential}</p>
                      <p className="text-xs text-[#666666] mt-1">{t.essentialDesc}</p>
                    </div>
                  </div>
                  
                  {/* Analytics cookies */}
                  <div className="flex items-start gap-4 p-4 bg-white border border-[#E5E5E5] rounded-lg">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-[#1A4D2E] focus:ring-[#1A4D2E] cursor-pointer"
                      data-testid="cookie-analytics-toggle"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-[#333333] text-sm">{t.analytics}</p>
                      <p className="text-xs text-[#666666] mt-1">{t.analyticsDesc}</p>
                    </div>
                  </div>
                  
                  {/* Preference cookies */}
                  <div className="flex items-start gap-4 p-4 bg-white border border-[#E5E5E5] rounded-lg">
                    <input
                      type="checkbox"
                      checked={preferences.preferences}
                      onChange={(e) => setPreferences(prev => ({ ...prev, preferences: e.target.checked }))}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-[#1A4D2E] focus:ring-[#1A4D2E] cursor-pointer"
                      data-testid="cookie-preferences-toggle"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-[#333333] text-sm">{t.preferences}</p>
                      <p className="text-xs text-[#666666] mt-1">{t.preferencesDesc}</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => setShowSettings(false)}
                    variant="outline"
                    className="border-[#E5E5E5]"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSavePreferences}
                    className="bg-[#1A4D2E] hover:bg-[#153d25] text-white gap-2"
                    data-testid="cookie-save-preferences"
                  >
                    <Check className="w-4 h-4" />
                    {t.save}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
